import type {
  SumsubAmlCase,
  SumsubAmlHit,
  SumsubAmlHitReview,
  SumsubAmlSource,
  SumsubApplicantData,
} from '@/integrations/Immersve/Sumsub/types';
import {
  fullName,
  type CaseHit,
  type CaseIdentityDetails,
  type HitRecord,
  type HitRecordKind,
  type HitVerdict,
  type IdentityFieldDiff,
} from './caseDetail';

export type {
  SumsubAmlCase,
  SumsubAmlHit,
  SumsubAmlHitReview,
  SumsubAmlSource,
  SumsubApplicantData,
} from '@/integrations/Immersve/Sumsub/types';

// Sumsub's per-hit review.matchStatus has no direct equivalent to our
// tri-state HitVerdict. 'potential_match' means the AML system itself still
// considers this an active, unresolved match (as opposed to
// 'false_positive'/'no_match', which it has ruled out) — mapped to
// 'true_positive' here rather than 'unknown' on that basis. This is an
// interpretive modeling choice, not a real field, and worth revisiting once
// a real human-reviewed potential_match case is available to check against.
const MATCH_STATUS_TO_VERDICT: Record<SumsubAmlHitReview['matchStatus'], HitVerdict> = {
  unknown: 'unknown',
  potential_match: 'true_positive',
  false_positive: 'false_positive',
  true_positive: 'true_positive',
  no_match: 'false_positive',
};

const SOURCE_TYPE_TO_KIND: Record<string, HitRecordKind> = {
  watchlist: 'SANCTION',
  media: 'ARTICLE',
  'adverse-media-v2': 'ARTICLE',
};

function kindForSourceType(type: string): HitRecordKind {
  return SOURCE_TYPE_TO_KIND[type] ?? (type.startsWith('pep') ? 'PEP_POSITION' : 'SOURCE');
}

function metaFor(source: SumsubAmlSource): string {
  const date = source.publishedDate ?? source.startDate;
  return [source.name, date?.slice(0, 10)].filter(Boolean).join(' · ');
}

function hostnameOf(url: string | null): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

// Sumsub's sources[] carry no id of their own — this key is synthesized
// (hit id + array index) purely so each becomes an addressable HitRecord in
// the UI; it is not part of the real payload.
function sourceToRecord(hitId: string, source: SumsubAmlSource, index: number): HitRecord {
  return {
    id: `${hitId}_source_${index}`,
    kind: kindForSourceType(source.type),
    title: source.title || source.name || hostnameOf(source.url) || 'Related record',
    meta: metaFor(source),
    url: source.url ?? undefined,
    fields: [
      ...(source.name ? [{ k: 'Source', v: source.name }] : []),
      { k: 'Type', v: source.type },
    ],
    snippet: source.annotation || undefined,
  };
}

function buildSumsubDiff(
  hit: SumsubAmlHit,
  identityDetails: CaseIdentityDetails
): IdentityFieldDiff[] {
  const rows: IdentityFieldDiff[] = [
    {
      field: 'Full name',
      subject: fullName(identityDetails.name) || 'Not supplied',
      matched: hit.name,
      status: 'unknown',
    },
  ];

  const matchedDob = hit.birthDates?.length
    ? hit.birthDates.join(', ')
    : hit.birthYears?.length
      ? hit.birthYears.join(', ')
      : undefined;
  if (identityDetails.dateOfBirth || matchedDob) {
    rows.push({
      field: 'Date of birth',
      subject: identityDetails.dateOfBirth || 'Not supplied',
      matched: matchedDob ?? '—',
      status: 'unknown',
    });
  }

  return rows;
}

export function sumsubToAmlHits(
  amlCase: SumsubAmlCase,
  identityDetails: CaseIdentityDetails
): CaseHit[] {
  return amlCase.hits.map((hit) => ({
    id: hit.id,
    matchedName: hit.name,
    verdict: MATCH_STATUS_TO_VERDICT[hit.review?.matchStatus ?? 'unknown'],
    hitTypes: hit.riskLabels,
    whitelisted: hit.review?.whitelisted || undefined,
    diff: buildSumsubDiff(hit, identityDetails),
    records: (hit.sources ?? []).map((source, index) => sourceToRecord(hit.id, source, index)),
  }));
}

// Matches the same case-level identityDetails shape as the real AmlReview
// contract (name parts, dateOfBirth, address) — see caseDetail.ts's
// CaseIdentityDetails. Names are kept exactly as Sumsub returns them
// (typically upper-case) rather than re-cased, since re-casing a real name
// risks introducing errors (e.g. "McDonald") that aren't in the source data.
export function sumsubToIdentityDetails(applicant: SumsubApplicantData): CaseIdentityDetails {
  return {
    name: { givenName: applicant.info.firstName, familyName: applicant.info.lastName },
    dateOfBirth: applicant.info.dob,
    address:
      applicant.info.addresses?.[0]?.formattedAddress ??
      applicant.fixedInfo?.addresses?.[0]?.formattedAddress ??
      '',
  };
}
